import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, ArrowLeft } from 'lucide-react';
import { BLOG_POSTS } from '../data/content';
import SEO from '../components/SEO';

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Article',
    headline: post.title, datePublished: post.date,
    author: { '@type': 'Organization', name: 'Laraé Logistics' },
  };

  // Render body markdown-lite (### headings, lists, paragraphs)
  const renderBody = () => post.body.split('\n').map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-3" />;
    if (t.startsWith('### ')) return <h3 key={i} className="font-outfit text-xl font-semibold text-ink mt-8 mb-3">{t.replace('### ', '')}</h3>;
    if (/^\d+\.\s/.test(t)) return <p key={i} className="font-manrope text-base text-ink/75 leading-relaxed">{t}</p>;
    if (t.startsWith('- ')) return <li key={i} className="font-manrope text-base text-ink/75 ml-5 list-disc">{t.slice(2)}</li>;
    if (t.startsWith('**') && t.endsWith('**')) return <p key={i} className="font-outfit font-semibold text-ink mt-4">{t.slice(2, -2)}</p>;
    return <p key={i} className="font-manrope text-base text-ink/75 leading-relaxed">{t}</p>;
  });

  return (
    <>
      <SEO title={`${post.title} | Laraé Logistics Blog`} description={post.excerpt} jsonLd={jsonLd} />
      <article className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-forest hover:underline mb-6"><ArrowLeft size={14} /> All articles</Link>
          <div className="flex items-center gap-3 text-xs text-ink/50 font-manrope mb-3"><Clock size={12} />{post.readMinutes} min read · {post.date}</div>
          <h1 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight leading-tight">{post.title}</h1>
          <p className="font-manrope text-lg text-ink/70 mt-5 leading-relaxed">{post.excerpt}</p>
          <div className="mt-10 space-y-1">{renderBody()}</div>

          <div className="mt-14 bg-sand/50 rounded-2xl p-7 text-center">
            <h3 className="font-outfit text-xl font-semibold text-ink">Need help with your move?</h3>
            <p className="font-manrope text-sm text-ink/65 mt-2">Get a fixed-price quote from Laraé Logistics in 60 seconds.</p>
            <Link to="/quote" className="inline-block mt-5 bg-forest text-white rounded-full px-7 py-3 font-medium">Get Free Quote</Link>
          </div>

          <div className="mt-14">
            <h3 className="font-outfit text-xl font-semibold text-ink mb-5">Related articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map(r => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="bg-white border border-sand rounded-xl p-4 hover:border-forest transition-all">
                  <div className="font-outfit text-sm font-semibold text-ink">{r.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
